import { Skeleton } from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";

import { useEventHostStats } from "@/features/event/hooks/use-events-query";

interface StatsProps {
  orgId: string;
}

interface StatCardProps {
  label: string;
  value: string;
  subLabel: string;
  dotColor: string;
  loading?: boolean;
}

function StatCard({ label, value, subLabel, dotColor, loading }: StatCardProps) {
  return (
    <div className="rounded-lg border border-border bg-background-secondary px-5 py-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className={cn("rounded-full w-2 h-2 shrink-0", dotColor)} />
        <span className="text-responsive-xs font-medium text-text-secondary uppercase tracking-wide">
          {label}
        </span>
      </div>
      {loading ? (
        <>
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-4 w-32" />
        </>
      ) : (
        <>
          <p className="text-3xl font-light text-text-primary font-literata">{value}</p>
          <p className="text-responsive-sm text-text-secondary">{subLabel}</p>
        </>
      )}
    </div>
  );
}

export function Stats({ orgId }: StatsProps) {
  const { t, formatCredit } = useI18n();
  const { data, isLoading } = useEventHostStats(orgId);

  const fillRate = data ? `${Math.round(data.avgFillRate * 100)}%` : "0%";
  const ticketsSold = data ? String(data.totalTicketsSold) : "0";
  const totalEvents = data ? String(data.totalEvents) : "0";
  const revenue = data ? formatCredit(data.totalRevenue) : formatCredit(0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard
        label={t("org.events.stats.totalEvents")}
        value={totalEvents}
        subLabel={`${data?.upcomingEvents ?? 0} sự kiện sắp tới`}
        dotColor="bg-red-400"
        loading={isLoading}
      />
      <StatCard
        label={t("org.events.stats.ticketsSold")}
        value={ticketsSold}
        subLabel={`/ ${data?.totalTicketsCap ?? 0} tổng sức chứa`}
        dotColor="bg-blue-400"
        loading={isLoading}
      />
      <StatCard
        label={t("org.events.stats.revenue")}
        value={revenue}
        subLabel="Tổng doanh thu"
        dotColor="bg-green-400"
        loading={isLoading}
      />
      <StatCard
        label={t("org.events.stats.fillRate")}
        value={fillRate}
        subLabel="Tỷ lệ lấp đầy trung bình"
        dotColor="bg-yellow-400"
        loading={isLoading}
      />
    </div>
  );
}
